export interface Gerador {
  id: string;
  nomeTitular: string;
  documento: string; // CPF ou CNPJ
  cep: string;
  logradouro: string;
  bairro: string;
  cidade: string;
  estado: string;
  concessionaria: string;
  potenciaInstalada: number; // kWp
  geracaoEstimada: number; // kWh/mês
  tarifaVenda: number; // R$/kWh
  custoInvestimento: number; // R$
  valorGeracaoEstimada?: number; // Calculado
  payback?: number; // Calculado
  valorKwhMes?: number; // Calculado
  creditosDisponiveis: number; // Saldo de créditos disponíveis
  criadoPor?: string; // ID do usuário que criou
  receitaTotal?: number; // Receita total com a venda de energia para clientes
}

export interface Cliente {
  id: string;
  tipo: 'energia' | 'solar'; // Type of client - energy or solar
  geradorId: string;
  nome: string;
  documento: string; // CPF ou CNPJ
  cep: string;
  logradouro: string;
  bairro: string;
  cidade: string;
  estado: string;
  concessionaria: string;
  numeroCliente: string;
  numeroInstalacao: string;
  gastoEnergia: number; // R$/mês
  tarifaEnergia: number; // R$/kWh
  tarifaGerador?: number; // R$/kWh - Cliente-specific generator rate
  consumoMedio?: number; // kWh/mês (calculado)
  gastoAnual?: number; // R$/ano (calculado)
  economiaEstimada?: number; // R$/mês (calculado)
  percentualEconomia?: number; // % (calculado)
  percentualEnergiaAdquirida?: number; // % (calculado)
  criadoPor?: string; // ID do usuário que criou
  valorCalculado?: number; // Valor calculado (Gasto Mensal * Tarifa)
  email?: string;
  celular?: string;
  status?: string;
  // Solar client specific fields
  tipoInstalacao?: 'residencial' | 'comercial' | 'industrial' | 'rural';
  potenciaInstalada?: number; // kWp
  areaOcupada?: number; // m²
  fabricantePaineis?: string;
  modeloPaineis?: string;
  fabricanteInversor?: string;
  modeloInversor?: string;
  dataInstalacao?: string;
}

export interface User {
  id: string;
  nome: string;
  email: string;
  senha?: string;
  role: UserRole;
  resetToken?: string;
  resetTokenExpiry?: number;
  profilePicture?: string;
  telefone?: string;
  endereco?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  dataNascimento?: string;
  bio?: string;
  permissions?: {
    canManageUsers?: boolean;
    canManagePartners?: boolean;
    canManageClients?: boolean;
    canManageGenerators?: boolean;
    canViewReports?: boolean;
    canAccessCalculator?: boolean;
    canManageSolarProjects?: boolean;
    canManageEnergyTrading?: boolean;
    canViewFinancials?: boolean;
    canEditFinancials?: boolean;
    canExportData?: boolean;
    canImportData?: boolean;
    canManageWorkflows?: boolean;
  };
}

export enum UserRole {
  ADMIN = "admin",
  VENDEDOR = "vendedor",
  PARCEIRO = "parceiro"
}

export const concessionarias = [
  "CEMIG",
  "CPFL",
  "EDP",
  "ENEL",
  "ENERGISA",
  "EQUATORIAL",
  "LIGHT",
  "NEOENERGIA"
];

export interface FinanceiroResumo {
  totalClientes: number;
  totalConsumo: number; // kWh/mês
  percentualUsoGerador: number; // %
  totalEconomia: number; // R$/mês
  gastoMedioClientes: number; // R$/mês
  receitaTotal: number; // Receita total com venda (R$/mês)
}

export interface DadosCliente {
  nome: string;
  telefone: string;
  email: string;
  cpf: string;
  logradouro?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  tipo?: 'energia' | 'solar';
}

export interface DadosIrradiacao {
  cep: string;
  latitude: number;
  longitude: number;
  irradiacaoMedia: number; // kWh/m²/dia
  minIrradiacao: number;
  maxIrradiacao: number;
  mesMin: string;
  mesMax: string;
  dadosMensais?: { mes: string; valor: number }[];
}

export interface DadosInstalacao {
  tipoTensao: 'baixa' | 'media';
  difusaoAzimutal: number; // graus
  inclinacaoTelhado: number; // graus
  tipologia: 'residencial' | 'comercial' | 'industrial' | 'rural';
  consumoMensal: number; // kWh/mês
  areaDeTelhado: number; // m²
  tipoTelhado: string;
  concessionaria: string;
  tipoTarifa: string;
  consumoHoraPonta?: number; // kWh/mês
  consumoForaPonta?: number; // kWh/mês
  demandaContratada?: number; // kW
  consumoMensalDetalhado?: boolean;
  dadosConsumoMensal?: { mes: string; valor: number }[];
}

export interface ParametrosFinanceiros {
  custoMaoDeObra: number;
  custoEngenharia: number;
  margemLucro: number;
  impostos: number;
  outrasDespesas: number;
  valorTotal?: number;
}

export interface EquipamentoSolar {
  id: string;
  tipo: 'painel' | 'inversor' | 'estrutura' | 'outros';
  marca: string;
  modelo: string;
  potencia?: number;
  quantidade: number;
  precoUnitario: number;
  fornecedor: string;
  disponibilidade: boolean;
  eficiencia?: number;
  garantia?: number;
  foto?: string;
  descricao?: string;
  dimensoes?: {
    altura?: number;
    largura?: number;
    profundidade?: number;
  };
}

export interface KitSolar {
  id: string;
  nome: string;
  potenciaSistema: number;
  paineis: EquipamentoSolar[];
  inversores: EquipamentoSolar[];
  estruturas: EquipamentoSolar[];
  outrosEquipamentos: EquipamentoSolar[];
  precoTotal: number;
  custoTelhado?: number;
  geracaoEstimada: number;
  paybackEstimado: number;
  fornecedor: string;
  tipoTelhado?: string;
}

export interface PropostaComercial {
  id: string;
  cliente: DadosCliente;
  dataGeracao: string;
  kitSolar: KitSolar;
  parametrosFinanceiros: ParametrosFinanceiros;
  precoFinal: number;
  economiaEstimada: number; // R$/mês
  retornoInvestimento: number; // meses
  criadoPor: string; // ID do usuário que criou
}

export interface CustomItem {
  id: string;
  name: string;
  description: string;
  quantity: number;
  unitPrice: number;
  type: 'painel' | 'inversor' | 'outro';
}

export interface ProjetoSolar {
  id: string;
  clienteId?: string;
  titulo: string;
  descricao: string;
  status: 'lead' | 'contato' | 'proposta' | 'negociacao' | 'fechado' | 'perdido';
  prioridade: 'baixa' | 'media' | 'alta';
  criadoEm: Date;
  atualizadoEm: Date;
  criadoPor: string;
  valorProposta?: number;
  formaPagamento?: 'boleto' | 'cartao' | 'financiamento' | 'misto';
  numeroProposta?: string;
  dadosIntegrador?: DadosIntegrador;
  dadosUnidadeConsumidora?: DadosUnidadeConsumidora;
  dadosEletricos?: DadosEletricos;
  dadosMecanicos?: DadosMecanicos;
  dadosFinanceiros?: DadosFinanceiros;
  dadosCompensacao?: DadosCompensacao;
  documentos?: Documento[];
  customItems?: CustomItem[]; // Added this property for the project's custom items
}

export interface DadosIntegrador {
  regional?: string;
}

export interface DadosUnidadeConsumidora {
  concessionaria?: string;
  titular?: string;
  cpfCnpj?: string;
  rg?: string;
  email?: string;
  celular?: string;
  numeroInstalacao?: string;
  numeroCliente?: string;
  endereco?: string;
  cep?: string;
  coordenadas?: string;
}

export interface DadosEletricos {
  classe?: 'Residencial' | 'Comercial' | 'Rural';
  tipoTensao?: 'Baixa' | 'Média';
  tipoPadrao?: 'Monofásico' | 'Bifásico' | 'Trifásico';
  nivelTensao?: '127/220' | '220/380';
  ramal?: 'Aéreo' | 'Subterrâneo';
  disjuntor?: string;
  aumentoCarga?: 'Não' | 'Sim';
  novoPadrao?: string;
  novoDisjuntor?: string;
  potenciaTrafo?: string;
}

export interface DadosMecanicos {
  telhado?: 'Metálico' | 'Cerâmica' | 'Fibromadeira' | 'Fibrocimento' | 'Solo' | 'Laje' | 'Carport';
  estrutura?: 'Metálico' | 'Madeira' | 'Cimentício';
  direcaoInclinacao?: 'Norte' | 'Sul' | 'Leste' | 'Oeste' | 'Leste_Oeste' | 'Norte_Sul' | 'Outra';
}

export interface DadosFinanceiros {
  formaPagamento?: 'Boleto' | 'Cartão' | 'Financiamento' | 'Misto';
  valorVenda?: number;
  numeroProposta?: string;
  observacao?: string;
}

export interface DadosCompensacao {
  uc1?: string;
  enderecoUc1?: string;
  porcentagem1?: string;
  uc2?: string;
  enderecoUc2?: string;
  porcentagem2?: string;
  uc3?: string;
  enderecoUc3?: string;
  porcentagem3?: string;
  observacao?: string;
}

export interface Documento {
  id: string;
  nome: string;
  tipo: string;
  url: string;
  dataUpload: Date;
  tamanho: number; // em bytes
  uploadedBy: string;
}

export const regionaisEcoefin = [
  "Ecoefin Aimorés",
  "Ecoefin Araújo",
  "Ecoefin AUSCA Solar",
  "Ecoefin Bahia",
  "Ecoefin Baixo Guandu",
  "Ecoefin Barra do Pirai",
  "Ecoefin Barreiro",
  "Ecoefin BH Leste",
  "Ecoefin BH Nordeste",
  "Ecoefin Carajás",
  "Ecoefin Caruaru",
  "Ecoefin CLA Solar",
  "Ecoefin Conceição da Barra",
  "Ecoefin Contagem",
  "Ecoefin Coronel Fabriciano",
  "Ecoefin Curvelo",
  "Ecoefin D' Solar Energy",
  "Ecoefin Divinópolis",
  "Ecoefin Eletrotins",
  "Ecoefin Fátima",
  "Ecoefin FP Solares",
  "Ecoefin Gerais",
  "Ecoefin GH Solar",
  "Ecoefin Goiania",
  "Ecoefin Grajaú",
  "Ecoefin Igarapé",
  "Ecoefin Infinity",
  "Ecoefin Ipatinga",
  "Ecoefin Itaipava",
  "Ecoefin Itaipu",
  "Ecoefin JF Norte",
  "Ecoefin João Monlevade",
  "Ecoefin João Pessoa Manaíra",
  "Ecoefin João Pessoa Paratibe",
  "Ecoefin Juiz de Fora Zona-Sul",
  "Ecoefin MAC Colatina",
  "Ecoefin Marabá",
  "Ecoefin Maria Goretti",
  "Ecoefin Matozinhos",
  "Ecoefin MD Solar",
  "Ecoefin Montes Belos",
  "Ecoefin Natural Sollar",
  "Ecoefin Nerópolis",
  "Ecoefin Niterói",
  "Ecoefin Norte MG",
  "Ecoefin Nova Iguaçu",
  "Ecoefin Olinda",
  "Ecoefin Orizânia",
  "Ecoefin Palmas",
  "Ecoefin Pampulha",
  "Ecoefin Paraíso",
  "Ecoefin Petrolina",
  "Ecoefin Petrópolis",
  "Ecoefin Porto Alegre",
  "Ecoefin Power Ed",
  "Ecoefin Riacho das Pedras",
  "Ecoefin Rio Solar",
  "Ecoefin Santa Luzia",
  "Ecoefin Serra do Cipó",
  "Ecoefin Sete Lagoas",
  "Ecoefin Sitter Solar",
  "Ecoefin SMTE",
  "Ecoefin Solar Recife",
  "Ecoefin Solutec",
  "Ecoefin SSA Solar",
  "Ecoefin Suns",
  "Ecoefin Timóteo",
  "Ecoefin Tocantins Solar",
  "Ecoefin Trindade",
  "Ecoefin Ubá",
  "Ecoefin Uberlândia",
  "Ecoefin Vale do Rio Doce",
  "Ecoefin Sudeste MG",
  "Ecoefin Vitorinos",
  "Ecoefin WG Soluções",
  "Ecoeletro",
  "Ecofin Suporte",
  "Soluções Energéticas Pampulha",
  "SunJob ADM"
];

export interface EstruturaConfig {
  tipo: string;
  valorPorKwp: number;
  descricao?: string;
}

export interface SistemaConfig {
  nome: string;
  descricao?: string;
  valorPorKwp: number;
  eficiencia: number;
  perdas: number;
  fatorSimultaneidade: number;
  fatorCapacidade: number;
  estruturas: EstruturaConfig[];
  marcaPadraoPainel?: string;
  modeloPadraoPainel?: string;
  fotoPadraoPainel?: string;
  marcaPadraoInversor?: string;
  modeloPadraoInversor?: string;
  fotoPadraoInversor?: string;
}

export const tiposEstrutura = [
  "metalica",
  "colonial",
  "fibromadeira",
  "fibrometal",
  "laje",
  "solo",
  "sem_estrutura"
];

export const nomesEstrutura: Record<string, string> = {
  metalica: "Metálica",
  colonial: "Colonial",
  fibromadeira: "Fibromadeira",
  fibrometal: "Fibrometal",
  laje: "Laje",
  solo: "Solo",
  sem_estrutura: "Sem Estrutura"
};
